import React, { ReactNode } from "react"
import seedrandom from "seedrandom"
import { CloudinaryImage } from "@cloudinary/url-gen"
import { AdvancedImage } from "@cloudinary/react"
import { fill } from "@cloudinary/url-gen/actions/resize"

import Section from "../template-parts/product-marketing/Section"
import ImageTextBlock from "../template-parts/product-marketing/ImageTextBlock"
import { GenericStringObj } from "../../types"
import { lazyLoadPreset } from "../../utils/cloudinaryImageUtils"

type Props = {
  city: string
  hasFixedOrMeetingPoints: boolean
  hasHomePickup: boolean
}

const firsts: string[] = [
  "%city% is an ideal place to learn to drive.",
  "%city% is a great place to learn to drive.",
  "%city% is an excellent place to develop driving skills.",
  "%city% features excellent learning opportunities for new drivers.",
  "%city% has a wide variety of roads, ideal for learning to drive.",
  "%city% has everything the new driver needs to learn to drive.",
  "With its diverse roadways and communities, %city% is an excellent place to learn to drive.",
  "It's hard to find a better place to learn to drive than %city%.",
  "%city% is an ideal place to develop your driving skills.",
  "What better place to learn to drive than %city%?",
  "%city% is an ideal place to take driving lessons.",
  "%city% is an excellent place to take driving lessons.",
]

const seconds: string[] = [
  "Within just a few minutes drive you can get practice in quiet residential areas, busy commercial areas, and major highways (and accompanying traffic).",
  "Within a short drive you can practice in quiet residential areas, busy commercial areas, and major highways (and accompanying traffic).",
  "Within the immediate area you can get practice in quiet residential areas, busy commercial areas, and major highways (and accompanying traffic).",
  "It is home to quiet residential areas, busy commercial zones and major highways (and accompanying traffic).",
  "There are quiet residential areas, busier commercial and business districts, and major freeways.",
  "From quiet residential streets to busy business areas, there is something for every step of the new driver journey within just a few minutes drive.",
  "Quiet residential areas, busy commercial zones and major freeways are all just a few minutes drive.",
  "You'll find a variety of different road types and intersections within a short drive, from quiet side streets to highways.",
  "There are low-speed residential areas, commercial areas with a variety of intersections, and faster highways, all within a few minutes drive.",
]

const thirds: string[] = [
  "Your driving instructor will meet you at a convenient central location in %city% - home pickup is also available in some lesson packages.",
  "We'll pick you up and drop you off where it's most convenient, like at home, school or work.",
  "We know you're busy, so we'll pick you up at home, work or school and can even drop you off where it is convenient within %city%.",
  "Some %city% lessons include pickup and drop off wherever is convenient, whether that's home, work or school.",
  "Some %city% lessons include pickup and drop off at home, work or school, making it easy to fit your lesson in with your schedule.",
  "Hassle-free scheduling - lessons in %city% with pickup and drop off at home, work or school available.",
]

const contentByCity: GenericStringObj = {
  Bakersfield:
    "Learning how to drive in Bakersfield, California can be an exciting and rewarding experience. As a Coastline Academy student, you'll have the opportunity to navigate some of Bakersfield's wide and well-maintained roads, plus learn how to drive safely on busy downtown streets. Whether in downtown Bakersfield, historic Oleander, Stockdale or Seven Oaks, our experienced instructors will guide you every step of the way to help you become a confident and safe driver for life.",
}

const getRandomSentence = (
  sentences: string[],
  sentNum: number,
  city: string,
) => {
  const rng = seedrandom(`${city}${sentNum.valueOf()}`)
  const rnd = rng()
  return sentences[Math.floor(rnd * sentences.length)]
}

const LocationContentSection = ({
  city,
  hasFixedOrMeetingPoints,
  hasHomePickup,
}: Props) => {
  const getContent = () => {
    if (city in contentByCity) return contentByCity[city]

    const contentStack: ReactNode[] = [
      <>
        {getRandomSentence(firsts, 1, city).replace("%city%", city)}{" "}
        {getRandomSentence(seconds, 2, city).replace("%city%", city)}
      </>,
    ]

    if (hasHomePickup) {
      if (hasFixedOrMeetingPoints) {
        contentStack.push(<strong>Some locations:</strong>)
      }
      contentStack.push(
        getRandomSentence(thirds, 3, city).replace("%city%", city),
      )
    }

    if (hasFixedOrMeetingPoints) {
      if (hasHomePickup) {
        contentStack.push(<strong>Other locations and lesson types:</strong>)
        contentStack.push(
          <>
            Some lessons in {city} originate from a convenient meeting point
            location. You can see our meeting points once you begin your search
            for availability.
          </>,
        )
      } else {
        contentStack.push(
          <>
            Lessons in {city} originate from a convenient meeting point
            location. You can see our meeting points once you begin your search
            for availability.
          </>,
        )
      }
    }

    return contentStack.map((c, i) => <p key={i}>{c}</p>)
  }

  return (
    <Section head={`Driving lessons in ${city} and surrounding areas`}>
      <ImageTextBlock
        image={
          <AdvancedImage
            cldImg={new CloudinaryImage(
              "vehicle-images/coastline-vehicle-new-branding-1_wzli9e",
              { cloudName: "coastline-academy" },
            )
              .resize(fill(600))
              .quality(80)
              .format("auto")}
            plugins={[lazyLoadPreset]}
            alt="Coastline Corolla"
            width={600} // for lazyload
            style={{ borderRadius: 60 }}
          />
        }
      >
        {getContent()}
      </ImageTextBlock>
    </Section>
  )
}

export default LocationContentSection
